import { STORAGE_KEYS } from '@/constants/storage-keys';

type Listener = () => void;

const KEY = STORAGE_KEYS.SESSION_TOKEN;

/** Storage can throw (private mode, blocked site data) — the app must still work without it. */
function read(storage: () => Storage): string | null {
  try {
    return storage().getItem(KEY);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, value: string | null): void {
  try {
    if (value === null) storage().removeItem(KEY);
    else storage().setItem(KEY, value);
  } catch {
    // Not persisted; the in-memory copy still signs the user in for this page load.
  }
}

const local = () => window.localStorage;
const session = () => window.sessionStorage;

let current: string | null = read(local) ?? read(session);
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

// Signing out in one tab signs out the others ("remember me" tokens live in localStorage).
window.addEventListener('storage', (event) => {
  if (event.key !== KEY) return;
  const next = read(local) ?? read(session);
  if (next === current) return;
  current = next;
  emit();
});

/**
 * The signed-in user's bearer token.
 *
 * - "Remember me" keeps it in localStorage, so it survives a browser restart.
 * - Otherwise it lives in sessionStorage and is gone when the tab closes.
 *
 * React reads it with `useSyncExternalStore(sessionTokenStore.subscribe, sessionTokenStore.get)`.
 */
export const sessionTokenStore = {
  get: (): string | null => current,

  set: (token: string, remember: boolean): void => {
    write(remember ? local : session, token);
    write(remember ? session : local, null);
    current = token;
    emit();
  },

  clear: (): void => {
    write(local, null);
    write(session, null);
    if (current === null) return;
    current = null;
    emit();
  },

  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
